import React, { useState, useRef, useEffect } from 'react';

const appleSpring = 'cubic-bezier(0.25, 1, 0.2, 1)';

interface SearchBarProps {
  status: 'default' | 'pushed' | 'working';
  value?: string;
  transcription?: string;
  selectedEmployee?: string;
  expandDirection?: 'up' | 'down';  // E1: kierunek rozwijania (domyślnie down)
  expandHeight?: number;              // E1: wysokość rozwinięcia w %
  onChange?: (val: string) => void;
  onSearchSubmit?: (val: string) => void;
  onSearchChange?: (val: string) => void;
  onVoiceSearchResult?: (val: string) => void;
  onNoteSubmit?: (val: string) => void;       // Enter w trybie notatki -> kolejka offline
}

export const SearchBar: React.FC<SearchBarProps> = ({
  status,
  value,
  transcription,
  selectedEmployee = '',
  expandDirection = 'down',  // E1: domyślnie w dół
  expandHeight = 160,        // E1: domyślna wysokość w px
  onChange,
  onSearchSubmit,
  onSearchChange,
  onVoiceSearchResult,
  onNoteSubmit
}) => {
  const isDictating = status === 'working';
  const isSearching = status === 'pushed';
  const [isLupkaPushed, setIsLupkaPushed] = useState(false);
  const [localValue, setLocalValue] = useState('');
  // Tryb pola tekstowego w stanie spoczynku (`status === 'default'`):
  //  - 'note'   (DOMYŚLNY) — „Mów lub napisz…”: Enter zapisuje notatkę do kolejki
  //             offline (onNoteSubmit). Focus/wpisywanie NIE przełącza w szukanie.
  //  - 'search' — „Znajdź…”: aktywowane WYŁĄCZNIE kliknięciem w lupkę (albo przez
  //             głosowe wyszukiwanie -> status 'pushed'). Enter/lupka uruchamiają
  //             `useSearchV2`, a wpisywanie filtruje listę (onSearchChange).
  const [mode, setMode] = useState<'note' | 'search'>('note');
  const inputRef = useRef<HTMLInputElement>(null);

  const textToShow = value !== undefined ? value : (transcription !== undefined ? transcription : localValue);
  // Głosowe wyszukiwanie (`status === 'pushed'`) to zawsze tryb szukania.
  const isSearchMode = mode === 'search' || isSearching;

  useEffect(() => {
    if (status === 'default') {
      setLocalValue('');
      // Powrót do spoczynku = powrót do domyślnego trybu tworzenia notatki.
      setMode('note');
      // Blur input żeby schować klawiaturę na iOS/Android
      inputRef.current?.blur();
    }
    // status === 'pushed' (głosowe wyszukiwanie) — klawiatura NIE wysuwa się
    // sama; user sam klika w pole. `isSearchMode` i tak jest już aktywne.
  }, [status]);

  // Gdy value z zewnątrz (wynik voice search) — zsynchronizuj localValue
  useEffect(() => {
    if (status === 'pushed' && value !== undefined) {
      setLocalValue(value);
    }
  }, [value, status]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    if (text.length <= 450) {
      setLocalValue(text);
      if (onChange) onChange(text);
      // Filtrowanie listy odpalamy TYLKO w trybie szukania — w trybie notatki
      // wpisywany tekst nie może zamieniać się w zapytanie do wyszukiwarki.
      if (isSearchMode && onSearchChange) onSearchChange(text);
      // Auto-reset: skasowanie całego wpisanego tekstu w trybie szukania
      // wraca do notatki — użytkownik nie utknie w "Brak wyników" po
      // przypadkowym kliknięciu lupki.
      if (mode === 'search' && text.length === 0) {
        setMode('note');
      }
    }
  };

  const submitSearch = () => {
    if (textToShow.trim() !== '' && onSearchSubmit) onSearchSubmit(textToShow);
    if (onVoiceSearchResult) onVoiceSearchResult(textToShow);
  };

  const handleLupkaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Lupka to jedyny sposób, by z pola „Mów lub napisz…” wejść w tryb szukania.
    if (!isSearchMode) {
      setMode('search');
      inputRef.current?.focus();
      return;
    }
    // Już w trybie szukania: pusty input + ponowny klik = powrót do notatki.
    if (textToShow.trim() === '') {
      setMode('note');
      return;
    }
    submitSearch();
  };

  const handleCancelSearch = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Twardy powrót do notatki: czyścimy pole i kasujemy filtr listy, żeby
    // nie zostać z martwym zapytaniem wyszukiwania w tle.
    setMode('note');
    setLocalValue('');
    if (onChange) onChange('');
    if (onSearchChange) onSearchChange('');
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    if (isSearchMode) {
      submitSearch();
      return;
    }
    // Tryb domyślny (notatka): Enter zapisuje treść w kolejce offline i czyści pole.
    if (textToShow.trim() !== '' && onNoteSubmit) onNoteSubmit(textToShow);
    setLocalValue('');
    if (onChange) onChange('');
    inputRef.current?.blur();
  };

  // E1: dynamiczne style w zależności od kierunku rozwijania
  const isExpandUp = expandDirection === 'up';
  const expandH = isDictating ? expandHeight : 35;

  return (
    // Outer wrapper: fixed height 35px — anchors edge in the flex layout
    <div
      className="relative w-full max-w-[360px] mx-auto pointer-events-none select-none"
      style={{ height: '35px', overflow: 'visible' }}
    >
    {/* Inner: grows from anchored edge (top for down, bottom for up) */}
    <div 
      className={`absolute left-0 w-full overflow-hidden pointer-events-auto ${isExpandUp ? 'bottom-0' : 'top-0'}`}
      style={{
        height: `${expandH}px`,
        borderRadius: isDictating ? '24px' : '17.5px',
        transition: isDictating
          ? `height 600ms ${appleSpring} 200ms, border-radius 600ms ${appleSpring} 200ms, box-shadow 300ms ease 200ms`
          : `height 600ms ${appleSpring}, border-radius 600ms ${appleSpring}, box-shadow 300ms ease`,
        backdropFilter: 'blur(16.5px)',
        background: isExpandUp 
          ? 'linear-gradient(0deg, rgba(25, 25, 25, 0.24) 0%, rgba(15, 15, 15, 0.4) 100%)'  // Odwrotny gradient dla up
          : 'linear-gradient(180deg, rgba(25, 25, 25, 0.24) 0%, rgba(15, 15, 15, 0.4) 100%)',
        boxShadow: isDictating
          ? 'inset 0px 1px 2px rgba(255, 255, 255, 0.2), 0px 0px 15px rgba(0, 255, 85, 0.15)'
          : isSearchMode
            ? 'inset 0px 1px 1.5px rgba(255, 255, 255, 0.15), 0px 0px 12px rgba(0, 170, 255, 0.18)'
            : 'inset 0px 1px 1.5px rgba(255, 255, 255, 0.15), 0px 4px 12px rgba(0, 0, 0, 0.15)',
        border: isDictating
          ? '1px solid rgba(0, 255, 85, 0.25)'
          : isSearchMode
            ? '1px solid rgba(0, 170, 255, 0.35)'
            : '1px solid rgba(255, 255, 255, 0.05)'
      }}
    >
      {/* 🔍 STAN 1 & 2: DEFAULT LUB SZUKANIE */}
      {!isDictating && (
        <div className="absolute inset-0 w-full h-full flex items-center px-5 justify-between">
          <div className={`flex items-center flex-1 h-full overflow-hidden relative ${mode === 'search' ? 'mr-16' : 'mr-8'}`}>
            
            {/* Prefiks: Imię gdy wybrana osoba, „Znajdź” WYŁĄCZNIE w trybie szukania
                (lupka / głos) — sam focus w trybie notatki go NIE pokazuje. */}
            {selectedEmployee ? (
              <span className="text-white/80 font-semibold text-base tracking-wide shrink-0 mr-1.5 animate-[fade-in_100ms_ease-out]">
                {selectedEmployee}
              </span>
            ) : isSearchMode ? (
              <span className="text-white/80 font-semibold text-base tracking-wide shrink-0 mr-1.5 animate-[fade-in_100ms_ease-out]">
                Znajdź
              </span>
            ) : null}

            {/* STAN DEFAULT: Gdy brak focusu i tekstu, widoczny jest wyłącznie placeholder.

                ANDROID — fokus pola i klawiatura ekranowa (frontend/src/components/SearchBar.tsx):
                • `userSelect: 'text'` KASUJE odziedziczone `select-none` z opakowania
                  wyżej. Na Androidzie `<input>` z `user-select: none` ma zepsute
                  stawianie karetki i tap-to-focus — pole „nie reaguje" na dotyk.
                • `touchAction: 'manipulation'` — natychmiastowy tap, bez 300 ms
                  oczekiwania na double-tap-zoom.
                • `onPointerDown`/`onTouchStart` robią WYŁĄCZNIE `stopPropagation()`
                  — NIGDY `preventDefault()`. `preventDefault` na zdarzeniu dotyku
                  wejścia zablokowałoby natywną klawiaturę ekranową. `stopPropagation`
                  chroni tylko przed gestami rodzica (np. PTT), które mogłyby przejąć
                  dotknięcie pola.
                • `enterKeyHint` / `inputMode` / `autoCapitalize` — sensowna
                  klawiatura (Enter = „Szukaj"/„Gotowe", zdaniowa wielkość liter). */}
            <input
              ref={inputRef}
              type="text"
              value={textToShow}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              placeholder={isSearchMode ? 'Szukaj w notatkach...' : 'Mów lub napisz...'}
              inputMode="text"
              enterKeyHint={isSearchMode ? 'search' : 'done'}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="sentences"
              spellCheck={false}
              className="bg-transparent text-white/60 font-light text-sm outline-none w-full tracking-wide p-0 border-none relative z-20 placeholder:text-white/40 placeholder:font-light placeholder:italic"
              style={{ caretColor: '#00FF55', userSelect: 'text', WebkitUserSelect: 'text', touchAction: 'manipulation' }}
            />
          </div>

          {mode === 'search' && (
            <button
              type="button"
              onClick={handleCancelSearch}
              aria-label="Anuluj wyszukiwanie"
              className="absolute right-[40px] w-[24px] h-[24px] flex items-center justify-center cursor-pointer z-30 text-white/50 hover:text-white/80 text-base leading-none bg-transparent border-none p-0"
            >
              ✕
            </button>
          )}

          <div
            onClick={handleLupkaClick}
            onMouseDown={() => setIsLupkaPushed(true)}
            onMouseUp={() => setIsLupkaPushed(false)}
            onMouseLeave={() => setIsLupkaPushed(false)}
            role="button"
            aria-pressed={isSearchMode}
            aria-label={isSearchMode ? 'Wyszukaj' : 'Przełącz na wyszukiwanie'}
            className="absolute right-[8px] w-[28px] h-[28px] flex items-center justify-center cursor-pointer z-30"
            style={{
              transition: `transform 100ms ${appleSpring}, filter 100ms ease`,
              transform: isLupkaPushed ? 'scale(0.97)' : 'scale(1)',
              filter: isLupkaPushed
                ? 'brightness(1.4) drop-shadow(0px 0px 6px rgba(255,255,255,0.3))'
                : isSearchMode
                  ? 'brightness(1.5) drop-shadow(0px 0px 4px rgba(0,255,85,0.35))'  // tryb szukania aktywny
                  : 'none'
            }}
          >
            <svg width="22" height="22" viewBox="320 5 26 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M338.613 21.0145C340.066 19.0325 340.716 16.5752 340.435 14.1342C340.153 11.6932 338.96 9.44846 337.095 7.84915C335.23 6.24984 332.829 5.41386 330.374 5.50847C327.918 5.60308 325.589 6.6213 323.852 8.35942C322.115 10.0975 321.099 12.4274 321.006 14.8828C320.913 17.3382 321.751 19.7382 323.351 21.6025C324.952 23.4669 327.198 24.6581 329.639 24.9378C332.08 25.2176 334.537 24.5653 336.518 23.1115H336.516C336.56 23.1715 336.609 23.229 336.663 23.284L342.438 29.059C342.72 29.3404 343.101 29.4986 343.499 29.4988C343.897 29.4989 344.279 29.341 344.56 29.0597C344.842 28.7784 345 28.3969 345 27.999C345 27.6011 344.842 27.2194 344.561 26.938L338.786 21.163C338.732 21.1086 338.675 21.06 338.613 21.0145ZM339 15.2485C339 16.3319 338.787 17.4047 338.372 18.4056C337.958 19.4065 337.35 20.316 336.584 21.0821C335.818 21.8482 334.908 22.4559 333.908 22.8705C332.907 23.2851 331.834 23.4985 330.75 23.4985C329.667 23.4985 328.594 23.2851 327.593 22.8705C326.592 22.4559 325.683 21.6025 323.852 21.0145ZM331.25 15.2485C331.25 16.3319 331.037 17.4047 330.622 18.4056Z" fill="url(#paint0_linear_25_425_clean)" fillOpacity="0.45"/>
            </svg>
          </div>
        </div>
      )}

      {/* 🎙️ STAN 3: DYKTAFON */}
      {isDictating && (
        <div 
          className={`w-full h-full flex flex-col p-5 overflow-y-auto custom-scrollbar leading-relaxed text-justify animate-[fade-in_250ms_ease-out] justify-start`}
        >
          <div className="w-full relative z-20">
            <span className="text-white/80 font-semibold text-base tracking-wide mr-1.5 inline">
              {selectedEmployee ? `Dyktuj ${selectedEmployee}` : 'Dyktuj'}
            </span>
            {textToShow && (
              <span className="text-white/60 font-light text-sm tracking-wide inline">
                {(() => {
                  // Strip prefix "Imię: " jeśli selectedEmployee ustawiony — unika duplikacji
                  if (selectedEmployee) {
                    const prefix = selectedEmployee + ': '
                    return textToShow.startsWith(prefix) ? textToShow.slice(prefix.length) : textToShow
                  }
                  return textToShow
                })()}
              </span>
            )}
          </div>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 0px; background: transparent; }
        @keyframes fade-in { from { opacity: 0; transform: translateY(-2px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
    </div>
  );
};